import React, { useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { CircularProgress, ThemeProvider } from '@material-ui/core';
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';

import { getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import {
  REDIRECT_TO_SESSION_KEY,
  LOGIN_ORG_SLUG_PLACEHOLDER,
  HANDOFF_NONCE_PARAM,
} from '@/utils/constants';
import { CLIENT_PARAM } from '@/utils/clientRegistry';
// eslint-disable-next-line max-len
import { appSuppliedRedirectTo } from '@/pages/PreOnboarding/steps/ActivateSleepio/hooks/usePostAuthRedirectTo';
import { buildHandoffParams, isAllowedAppHandoffTarget } from '@/utils/urlHelpers';
import { decodeHandoffPayload, HANDOFF_PAYLOAD_PARAM } from '@/utils/handoffPayload';
import { logError } from '@/utils/logError';
import { ActiveFeatureFlag, isBooleanFeatureFlagEnabled } from '@/hooks/usePostHogFeatureFlags';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { linkPostHogIdentity } from '@/utils/posthog';

const SleepioEmailCallback: React.FC = () => {
  const history = useHistory();
  const { lob } = useParams<{ lob: string }>();
  const { search } = useLocation();
  const posthog = usePostHog();
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, { fetchPolicy: 'network-only' });
  const supabaseAuthEnabled = isBooleanFeatureFlagEnabled(
    useFeatureFlagVariantKey(ActiveFeatureFlag.CAP_USE_SUPABASE_AUTH),
  );

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const params = new URLSearchParams(search);

        // `client` and `redirectTo` ride together in the opaque `hp` param on this one
        // hop — the only one that goes through Iterable's click-tracking wrapper, which
        // can fracture an ordinary query string (see handoffPayload.ts). Restore them to
        // their plain names on `params` so every helper below (and the callback URL this
        // screen builds for the rest of the flow, which never crosses another vendor hop)
        // stays exactly as written for the ordinary shape. A URL with no `hp` — a legacy
        // link, or a CAP deployment that has not learned this yet — falls straight through
        // unchanged, since `params` already carries whatever plain form it used instead.
        const handoffPayload = decodeHandoffPayload(params.get(HANDOFF_PAYLOAD_PARAM));
        if (handoffPayload?.client) {
          params.set(CLIENT_PARAM, handoffPayload.client);
        }
        if (handoffPayload?.redirectTo) {
          params.set('redirectTo', handoffPayload.redirectTo);
        }
        const effectiveSearch = params.toString();

        const redirectToParam = params.get('redirectTo');
        // The handoff check, not the scheme check: this writes the key that the resolver
        // later reads, and the resolver only honours handoff targets. Saving a looser value
        // would just put junk in a key shared with the Spark flows.
        if (redirectToParam && isAllowedAppHandoffTarget(redirectToParam)) {
          try {
            sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectToParam);
          } catch {
            // Guarded for the same reason the matching read in usePostAuthRedirectTo is: a
            // browser with storage locked down throws on access and this app has no error
            // boundary. Unguarded, the throw lands in the catch below and tells a member with
            // a perfectly good session that verification failed. Losing the saved copy only
            // costs us one of three sources for the address.
          }
        }

        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        const { data: { session } } = await getSupabaseClient().auth.getSession();

        if (!session) {
          history.replace('/activate/sleepio/access-code', { error: 'email_verification_failed' });
          return;
        }

        const phDistinctId = session.user?.user_metadata?.phDistinctId;
        if (typeof phDistinctId === 'string' && phDistinctId) {
          posthog?.identify(phDistinctId);
        }

        if (supabaseAuthEnabled === null) { return; }

        if (!supabaseAuthEnabled) {
          history.replace('/activate/sleepio/employers');
          return;
        }

        // Which endpoint minted this magic link, not a guess reconstructed from the
        // session afterward. /api/auth/login stamps `flow=login` on every callback URL it
        // builds (vercel-auth-functions api/auth/login.ts); activate/email's buildRedirectTo
        // never sets `flow` at all. The two VAF endpoints already know unambiguously which
        // one issued the link, and this is that answer, carried on the one URL both endpoints
        // control end to end — nothing here has to infer it a second time.
        //
        // The previous approach guessed from the JWT's entitlement status
        // (`!accessCode && entitlementStatus === 'active'`), on the assumption that only a
        // returning member's entitlement could already read `active`. That assumption breaks
        // for a direct-access org that grants `active` immediately at sign-up rather than
        // staying `pending` until a separate confirm step — the exact org this was caught on.
        // It is also the same class of mistake PAT-1286 already named: guessing new-vs-
        // returning from state that does not reliably distinguish the two, rather than from
        // which request actually asked.
        const isReturningUser = params.get('flow') === 'login';

        // Sign-up's magic link carries org_slug; login's doesn't — /api/auth/login builds
        // that URL and has no org to put in it. Empty leaves a `//` in the path below,
        // which matches none of the /activate/sleepio-canopy/:lob/:org_slug/:intent
        // routes, so the user lands on an unrelated screen.
        //
        // Only login gets a filler. Its route never reads the org — it goes to /complete,
        // which needs the session. Sign-up reaches enrollNewUserInCortex, which passes
        // the org to activatePatient, so a filler there would enroll someone into it.
        const orgSlugParam = params.get('org_slug');
        const orgSlug = orgSlugParam || (isReturningUser ? LOGIN_ORG_SLUG_PLACEHOLDER : '');
        const intent = isReturningUser ? 'login' : 'sign-up';

        // A sign-up link always carries an org, so reaching here without one means either
        // a returning user misread as new, or a link built by something that skipped it.
        // Either way the path below gets an empty segment, matches no route, and drops the
        // user on an unrelated screen — the same failure this fallback fixes for login,
        // which we can't repair the same way (a filler would enroll them into it). The one
        // thing we can do is not let it happen silently, which is how it went unnoticed.
        if (!orgSlugParam && !isReturningUser) {
          logError('[SleepioEmailCallback]', 'sign-up callback has no org_slug; route will not match', undefined, {
            level: 'warning',
          });
        }
        // The same resolution every other screen in the flow uses: this screen's own URL
        // first, then the saved copy, then the flow's client name. It no longer depends on
        // whether we think the member is new or returning — that guess is what sent
        // returning members to a screen holding the wrong credential.
        //
        // `appSuppliedRedirectTo`, not `resolvePostAuthRedirectTo`: absent has to stay
        // absent here, same as ActivationComplete.tsx's own read of this value. This is the
        // first screen the magic link lands on, so substituting the bare default when
        // nothing was actually supplied doesn't just affect this screen — it writes that
        // default onto the next URL as a real `redirectTo` param, and every screen downstream
        // then reads it back as genuinely supplied, all the way to ActivationComplete trying
        // to open it. That's exactly the "desktop browser attempts a deep link" failure the
        // default form exists to avoid, just moved one hop earlier.
        //
        // All three handoff values, not just the address. This is the hop where the email's
        // fresh tab means session storage holds nothing, so anything not put on the URL here
        // is gone for the rest of the flow — which is how the client name and the flat nonce
        // were being lost between the auth service writing them and ActivationComplete
        // looking for them.
        const redirectParam = buildHandoffParams(
          appSuppliedRedirectTo({ search: effectiveSearch }),
          params.get(CLIENT_PARAM),
          params.get(HANDOFF_NONCE_PARAM),
        );

        const email = session.user?.email;
        if (email) {
          // Fire-and-forget: React Router navigation keeps the Apollo client alive,
          // so the in-flight query completes after the component unmounts.
          linkPostHogIdentity(getDistinctId, posthog, email, '[SleepioEmailCallback]');
        }

        history.replace(`/activate/sleepio-canopy/${lob}/${orgSlug}/${intent}/email/callback${redirectParam}`);
      } catch (err) {
        logError('[SleepioEmailCallback]', 'Unexpected error', err);
        history.replace('/activate/sleepio/access-code', { error: 'email_verification_failed' });
      }
    };

    handleCallback();
  }, [history, lob, search, supabaseAuthEnabled, getDistinctId, posthog]);

  return (
    <ThemeProvider theme={getTheme('sleepio')}>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default SleepioEmailCallback;
